import { mapGetters } from 'vuex';
import { normalizeErrorMessage } from '@/utils/error-message';
import { getEnrolledCount, getQuota, getRemainingQuota } from '@/utils/enrollmentQuota';
import mitraCourseListFilter from '@/mixins/mitraCourseListFilter';
import {
  APPROVAL_STEP_SLUG_OPTIONS,
  COURSE_TYPE_BOOLEAN_OPTIONS,
  ENROLLMENT_COURSE_STATUS_OPTIONS
} from '@/constants/mitraFilters';

export default {
  name: 'MitraEnrollment',
  mixins: [mitraCourseListFilter],
  data() {
    return {
      selectedCourse: null,
      selectedBatch: null,
      selectedBatchIndex: 0,
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 50, 100],
      filterModalId: 'modal-filter-mitra-enrollment',
      statusQueryKey: 'status',
      filters: {
        search: (this.$route && this.$route.query && this.$route.query.search) || '',
        status: null,
        approval_step: null,
        course_type: null
      },
      statusOptions: ENROLLMENT_COURSE_STATUS_OPTIONS,
      approvalStepOptions: APPROVAL_STEP_SLUG_OPTIONS,
      courseTypeOptions: COURSE_TYPE_BOOLEAN_OPTIONS,
      selectedPeriod: null,
      periodsLoading: false,
      isDownloadingGlobalTemplate: false,
      isUploadingGlobalTemplate: false,
      imageBaseUrl: process.env.VUE_APP_API_URL_UGEMS_UNIV,
      targetTrainingEventId: null,
      hasOpenedDeepLink: false
    };
  },
  computed: {
    ...mapGetters('mitraEnrollment', ['courses', 'pagination', 'loading', 'error', 'summary', 'summaryLoading']),
    ...mapGetters('idpPeriod', ['IdpPeriod']),
    
    // Period dropdown options from store
    periodOptions() {
      const data = this.IdpPeriod;

      const list = data?.data;
      if (!list || !list.length) return [];

      return list.map(p => ({
        label: this.formatPeriodLabel(p.start_date, p.end_date),
        value: p.id
      }));
    },
    
    isLoading() {
      return this.loading;
    },
    
    filteredCourses() {
      let filtered = this.courses || [];
      
      if (this.filters.search) {
        const searchLower = this.filters.search.toLowerCase();
        filtered = filtered.filter(course => 
          course.title.toLowerCase().includes(searchLower) ||
          course.description?.toLowerCase().includes(searchLower) ||
          course.provider_name?.toLowerCase().includes(searchLower)
        );
      }
      
      return filtered;
    },
    
    paginatedCourses() {
      return this.courses || [];
    },
    
    totalItems() {
      return this.pagination?.total || 0;
    },
    
    totalPages() {
      return this.pagination?.totalPage || 0;
    },
    
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        
        if (current > 3) {
          pages.push('...');
        }
        
        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);
        
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
        
        if (current < total - 2) {
          pages.push('...');
        }
        
        pages.push(total);
      }
      
      return pages;
    }
  },
  
  watch: {
    'filters.search'() {
      this.currentPage = 1;
      this.fetchCourses();
    },
    selectedPeriod() {
      this.fetchSummary();
      this.fetchCourses();
    },
    currentPage() {
      this.fetchCourses();
    }
  },
  
  methods: {
    async fetchCourses() {
      try {
        const params = {
          page: this.currentPage,
          limit: this.perPage
        };
        
        if (this.filters.search) {
          params.search = this.filters.search;
        }

        this.applyCourseListFilterParams(params);
        
        if (this.selectedPeriod) {
          params.idp_period = this.selectedPeriod;
        }

        if (this.targetTrainingEventId) {
          params.training_event_id = this.targetTrainingEventId;
        }
        
        const courses = await this.$store.dispatch('mitraEnrollment/getCourses', params);
        this.openDeepLinkTarget(courses);
      } catch (error) {
        console.error('Error fetching courses:', error);
        this.$swal({
          title: 'Gagal Memuat Data',
          text: normalizeErrorMessage(error, 'Data pelatihan belum berhasil dimuat. Silakan coba lagi.'),
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      }
    },
    
    async fetchSummary() {
      if (!this.selectedPeriod) return;
      
      try {
        await this.$store.dispatch('mitraEnrollment/getSummary', {
          idp_period: this.selectedPeriod
        });
      } catch (error) {
        console.error('Error fetching summary:', error);
      }
    },
    
    async viewCourseDetail(course) {
      this.selectedCourse = course;
      // Show batch list modal - fetching will be handled by modal's @shown event
      this.$bvModal.show('modal-batch-list');
    },

    openDeepLinkTarget(courses = this.courses) {
      if (this.hasOpenedDeepLink) return;

      const targetCourseId = this.$route?.query?.course_id;
      if (!this.targetTrainingEventId && !targetCourseId) return;

      const list = courses || [];
      let course = null;

      if (targetCourseId) {
        course = list.find((item) => String(item.id) === String(targetCourseId));
      } else if (this.targetTrainingEventId && list.length === 1) {
        course = list[0];
      }

      if (!course && this.$route?.query?.search) {
        const needle = String(this.$route.query.search).toLowerCase();
        const matches = list.filter((item) =>
          String(item.title || "").toLowerCase().includes(needle)
        );
        if (matches.length === 1) {
          course = matches[0];
        }
      }

      if (!course) return;

      this.hasOpenedDeepLink = true;
      this.viewCourseDetail(course);
    },
    
    handleBatchSelected({ batch, index }) {
      this.selectedBatch = batch;
      this.selectedBatchIndex = index;
      
      // Show participant list modal
      this.$bvModal.show('modal-participant-list');
    },
    
    async refreshBatches() {
      // Re-fetch batches for the selected course
      if (this.selectedCourse) {
        try {
          const params = {};
          if (this.selectedPeriod) {
            params.idp_period = this.selectedPeriod;
          }
          
          await this.$store.dispatch('mitraEnrollment/getBatches', {
            courseId: this.selectedCourse.id,
            params
          });
        } catch (error) {
          console.error('Error refreshing batches:', error);
        }
      }
    },
    
    changePage(page) {
      if (page === '...' || page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    },
    
    onPerPageChange() {
      this.currentPage = 1;
      this.fetchCourses();
    },
    
    clearFilters() {
      this.filters = {
        ...this.filters,
        search: '',
        status: null,
        approval_step: null,
        course_type: null
      };
      this.currentPage = 1;
    },

    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },

    buildUploadErrorTable(errors = []) {
      if (!errors.length) return '';

      const rows = errors.slice(0, 50).map((item) => `
        <tr>
          <td style="padding: 10px; border: 1px solid #e5e7eb; width: 70px;">${this.escapeHtml(item.row || '-')}</td>
          <td style="padding: 10px; border: 1px solid #e5e7eb; width: 150px;">${this.escapeHtml(item.sheet || '-')}</td>
          <td style="padding: 10px; border: 1px solid #e5e7eb; width: 140px;">${this.escapeHtml(item.nik || '-')}</td>
          <td style="padding: 10px; border: 1px solid #e5e7eb; width: 110px;">${this.escapeHtml(item.tna_id || '-')}</td>
          <td style="padding: 10px; border: 1px solid #e5e7eb; color: #b91c1c; min-width: 420px; line-height: 1.45;">${this.escapeHtml(item.message || item.reason || 'Data tidak valid')}</td>
        </tr>
      `).join('');

      const remaining = errors.length > 50
        ? `<p class="mb-0 mt-2 text-muted">Masih ada ${errors.length - 50} error lain. Perbaiki error dari atas dulu lalu upload ulang.</p>`
        : '';

      return `
        <div class="mt-3 text-left">
          <p class="mb-2" style="color: #b91c1c;"><strong>Detail error yang perlu diperbaiki:</strong></p>
          <div style="max-height: 360px; overflow: auto; border: 1px solid #e5e7eb; border-radius: 6px;">
            <table style="width: 100%; min-width: 920px; font-size: 13px; border-collapse: collapse;">
              <thead>
                <tr style="background: #fee2e2;">
                  <th style="padding: 10px; border: 1px solid #e5e7eb; text-align: left; width: 70px;">Row</th>
                  <th style="padding: 10px; border: 1px solid #e5e7eb; text-align: left; width: 150px;">Sheet</th>
                  <th style="padding: 10px; border: 1px solid #e5e7eb; text-align: left; width: 140px;">NIK</th>
                  <th style="padding: 10px; border: 1px solid #e5e7eb; text-align: left; width: 110px;">TNA ID</th>
                  <th style="padding: 10px; border: 1px solid #e5e7eb; text-align: left; min-width: 420px;">Pesan Error</th>
                </tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>
          </div>
          ${remaining}
        </div>
      `;
    },

    async downloadGlobalTemplate() {
      if (this.isDownloadingGlobalTemplate) return;

      this.isDownloadingGlobalTemplate = true;
      try {
        const params = {};
        if (this.selectedPeriod) {
          params.idp_period = this.selectedPeriod;
        }

        const response = await this.$store.dispatch('mitraEnrollment/downloadGlobalEnrollmentTemplate', params);
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Enrollment_All_Template.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        const errorMessage = normalizeErrorMessage(error, 'Template belum berhasil didownload. Silakan coba lagi.');
        this.$swal({
          title: 'Gagal Download Template',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      } finally {
        this.isDownloadingGlobalTemplate = false;
      }
    },

    triggerGlobalUpload() {
      if (this.isUploadingGlobalTemplate) return;
      this.$refs.globalEnrollmentFileInput?.click();
    },

    async handleGlobalEnrollmentFileChange(event) {
      const file = event.target.files?.[0];
      event.target.value = '';

      if (!file) return;

      if (!file.name.toLowerCase().endsWith('.xlsx')) {
        this.$swal({
          title: 'Tipe File Tidak Valid',
          text: 'Unggah file Excel .xlsx',
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        this.$swal({
          title: 'File Terlalu Besar',
          text: 'Ukuran file maksimal 5 MB',
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return;
      }

      this.isUploadingGlobalTemplate = true;
      try {
        const formData = new FormData();
        formData.append('file', file);
        if (this.selectedPeriod) {
          formData.append('idp_period', this.selectedPeriod);
        }

        const response = await this.$store.dispatch('mitraEnrollment/bulkUploadGlobalEnrollment', formData);
        const result = response?.data || {};
        const errors = result.errors || [];
        const batches = result.batches || [];

        this.$swal({
          title: errors.length ? 'Upload Selesai dengan Error' : 'Upload Berhasil',
          html: `
            <div class="text-left">
              <p class="mb-1"><strong>Berhasil submit:</strong> ${result.submitted || 0} peserta</p>
              <p class="mb-1"><strong>Email diperbarui:</strong> ${result.updated_emails || 0}</p>
              <p class="mb-1"><strong>Batches:</strong> ${batches.length}</p>
              <p class="mb-0"><strong>Error:</strong> ${errors.length}</p>
              ${this.buildUploadErrorTable(errors)}
            </div>
          `,
          icon: errors.length ? 'warning' : 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: errors.length ? '#f6c343' : '#00c9a7',
          width: errors.length ? '1100px' : '720px'
        });

        await Promise.all([
          this.fetchSummary(),
          this.fetchCourses()
        ]);
      } catch (error) {
        const errorMessage = error.code === 'ECONNABORTED'
          ? 'Upload masih diproses terlalu lama dan melewati batas waktu koneksi. Silakan coba upload ulang dengan file yang sama; jika data sudah sebagian masuk, sistem akan menampilkan detail duplikat/error pada hasil upload berikutnya.'
          : error.response?.data?.error || error.response?.data?.message || error.message || 'Gagal upload file';
        this.$swal({
          title: 'Upload Gagal',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      } finally {
        this.isUploadingGlobalTemplate = false;
      }
    },
    
    handleImageError(event) {
      event.target.src = 'https://dummyimage.com/300x200/FAFAFA/000000&text=Course';
    },

    handleImageProviderError(event) {
      event.target.src = 'https://dummyimage.com/16x16/ffffff/000000&text=Provider+title';
    },
    
    getTrainingTypeIcon(trainingType) {
      const type = trainingType?.toLowerCase() || '';
      
      if (type.includes('certification') && !type.includes('semi')) {
        return 'fe fe-file-text';
      } else if (type.includes('semi')) {
        return 'fe fe-shield';
      } else if (type.includes('upskilling')) {
        return 'fe fe-trending-up';
      } else if (type.includes('reskilling')) {
        return 'fe fe-refresh-cw';
      } else if (type.includes('mandatory')) {
        return 'fe fe-alert-circle';
      } else {
        return 'fe fe-book-open';
      }
    },
    
    hasQuotaInfo(course) {
      return course?.quota != null
        || course?.enrolled_count != null
        || course?.remaining_quota != null
        || course?.is_quota_full != null;
    },

    getEnrolledCount,
    getQuota,
    getRemainingQuota,

    getStatusBadgeClass(status) {
      const classes = {
        Schedule: 'badge bg-success',
        'No Schedule': 'badge bg-warning',
        Completed: 'badge bg-info',
        Cancelled: 'badge bg-danger',
        SCHEDULE: 'badge bg-success',
        'NO SCHEDULE': 'badge bg-warning',
        COMPLETED: 'badge bg-info',
        CANCELLED: 'badge bg-danger'
      };
      return classes[status] || 'badge bg-secondary';
    },
    
    formatPeriodLabel(startDate, endDate) {
      if (!startDate || !endDate) return '';
      
      const fmt = (dateStr) => {
        const d = new Date(dateStr);
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
      };
      
      return `${fmt(startDate)} - ${fmt(endDate)}`;
    }
  },
  
  async mounted() {
    console.log('✅ Mitra Enrollment page mounted');
    this.targetTrainingEventId = this.$route?.query?.training_event_id || null;
    
    // Fetch IDP periods first
    try {
      const result = await this.$store.dispatch('idpPeriod/getIdpPeriod');
      console.log('[idpPeriod] dispatch result:', result);

      // Set period with vstatus 'open' as default if available
      const periods = this.IdpPeriod?.data || [];
      const openPeriod = periods.find(p => p.vstatus === 'open');
      
      if (openPeriod) {
        this.selectedPeriod = openPeriod.id;
      } else if (this.periodOptions.length > 0) {
        // Fallback to first period if no open period found
        this.selectedPeriod = this.periodOptions[0].value;
      }
    } catch (error) {
      console.error('Error fetching idp periods:', error);
    }
    
    // Fetch courses
    this.fetchCourses();
  }
};
